/**
 * The Uber catalogue review inside Push Uber Menu (Jason, 26 Oct 2026). Two lists, both settled
 * here and nowhere else, then pushed with the menu:
 * - NEW products: Lightspeed products created since the catalogue was set up, in a mapped
 *   category or tag. Add (goes on Uber at the formula price) or Decline (stops being offered).
 * - MISMATCHING: Uber items whose Lightspeed name / description / image / category / tags moved.
 *   Per field: use Lightspeed's, keep Uber's, or amend (type the Uber value). An item no longer
 *   in Lightspeed: take it off Uber, or keep it.
 * Anything left undecided stays in the alert on the Uber Menu page for next time.
 */
import React, { useState } from 'react';
import {
  Box, Typography, Button, Chip, Accordion, AccordionSummary, AccordionDetails,
  ToggleButton, ToggleButtonGroup, TextField,
} from '@mui/material';
import { ExpandMore as ExpandMoreIcon, NewReleases as NewIcon, CompareArrows as MismatchIcon } from '@mui/icons-material';

export interface ReviewPlacement { mappingId: number | null; menu: string; section: string; sectionPos?: number }
export interface ReviewNewProduct {
  sku: string; skuKey: string; name: string; description: string; imageUrl: string | null;
  price: number | null; suggestedCents: number; category: string | null; tags: string[];
  placements: ReviewPlacement[]; createdAt: string | null;
}
export interface ReviewMismatchField { field: string; uber: string | null; was: string | null; now: string | null }
export interface ReviewMismatch { sku: string; skuKey: string; name: string; fields: ReviewMismatchField[] }
export interface CatalogueReview { newProducts: ReviewNewProduct[]; mismatches: ReviewMismatch[]; lsFetchedAt: string | null; seededAt: string | null }

export type FieldDecision = 'accept' | 'keep' | 'remove' | { amend: string };
export interface CatalogueDecisions {
  newProducts: Record<string, 'add' | 'decline'>;
  mismatches: Record<string, Record<string, FieldDecision>>;
}
export const emptyDecisions = (): CatalogueDecisions => ({ newProducts: {}, mismatches: {} });

/** How many choices were made (for the push button / confirm dialog). */
export function countDecisions(d: CatalogueDecisions): { added: number; declined: number; fields: number } {
  const v = Object.values(d.newProducts);
  return {
    added: v.filter((x) => x === 'add').length,
    declined: v.filter((x) => x === 'decline').length,
    fields: Object.values(d.mismatches).reduce((n, m) => n + Object.keys(m).length, 0),
  };
}

const GREEN = '#2e7d32';
const PAGE = 50;
const LABEL: Record<string, string> = { name: 'Name', description: 'Description', image: 'Image', category: 'Category', tags: 'Tags', gone: 'Gone from Lightspeed' };
const rand = (cents: number) => `R${(cents / 100).toFixed(2)}`;
const isImage = (f: string) => f === 'image';

function Thumb({ url }: { url: string | null }) {
  return url
    ? <Box component="img" src={url} alt="" loading="lazy" sx={{ width: 40, height: 40, objectFit: 'cover', borderRadius: 1, border: '1px solid #e0e0e0', verticalAlign: 'middle' }} />
    : <Typography component="span" variant="caption" color="text.secondary">no image</Typography>;
}

function Value({ field, value }: { field: string; value: string | null }) {
  if (isImage(field)) return <Thumb url={value} />;
  return <Typography component="span" variant="body2" sx={{ wordBreak: 'break-word' }}>{value || <em>(empty)</em>}</Typography>;
}

interface Props {
  review: CatalogueReview;
  decisions: CatalogueDecisions;
  onChange: (d: CatalogueDecisions) => void;
}

const UberCatalogueReview: React.FC<Props> = ({ review, decisions, onChange }) => {
  const [newShown, setNewShown] = useState(PAGE);
  const [mmShown, setMmShown] = useState(PAGE);
  const { newProducts, mismatches } = review;
  if (newProducts.length === 0 && mismatches.length === 0) {
    return (
      <Box sx={{ mb: 2, p: 1.5, border: '1px solid #c8e6c9', borderRadius: 2, bgcolor: '#f1f8e9' }}>
        <Typography variant="body2" sx={{ color: GREEN }}>
          No new products and nothing mismatching Lightspeed.
        </Typography>
      </Box>
    );
  }

  const setNew = (key: string, v: 'add' | 'decline' | null) => {
    const next = { ...decisions.newProducts };
    if (v) next[key] = v; else delete next[key];
    onChange({ ...decisions, newProducts: next });
  };
  const allNew = (v: 'add' | 'decline' | null) => onChange({
    ...decisions,
    // a product with no Lightspeed price cannot go on Uber yet: Add all leaves it to decide later
    newProducts: v ? Object.fromEntries(newProducts.filter((p) => v === 'decline' || p.suggestedCents > 0).map((p) => [p.skuKey, v])) : {},
  });
  const setField = (key: string, field: string, v: FieldDecision | null) => {
    const item = { ...(decisions.mismatches[key] || {}) };
    if (v) item[field] = v; else delete item[field];
    const next = { ...decisions.mismatches };
    if (Object.keys(item).length) next[key] = item; else delete next[key];
    onChange({ ...decisions, mismatches: next });
  };
  const allFields = (v: 'accept' | 'keep' | null) => onChange({
    ...decisions,
    mismatches: v ? Object.fromEntries(mismatches.map((m) => [m.skuKey, Object.fromEntries(m.fields.map((f) => [f.field, f.field === 'gone' ? 'keep' : v]))])) : {},
  });
  const choice = (d: FieldDecision | undefined) => (d && typeof d === 'object' ? 'amend' : d ?? null);

  return (
    <Box sx={{ mb: 2 }}>
      {newProducts.length > 0 && (
        <Accordion sx={{ mb: 1.5, borderRadius: '8px !important', border: '1px solid #a5d6a7', '&:before': { display: 'none' } }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <NewIcon sx={{ color: GREEN, fontSize: 20 }} />
              <Typography variant="subtitle2" sx={{ fontWeight: 700, color: GREEN }}>
                {newProducts.length} new product{newProducts.length === 1 ? '' : 's'} in Lightspeed
              </Typography>
              <Chip size="small" label={`${countDecisions(decisions).added} to add · ${countDecisions(decisions).declined} declined`} />
            </Box>
          </AccordionSummary>
          <AccordionDetails sx={{ pt: 0 }}>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
              Created in Lightspeed since the Uber catalogue was set up, in a mapped category or tag. Added ones go on Uber with
              Lightspeed's name, description and image at the formula price (change any of it on the Uber Menu page after).
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
              <Button size="small" onClick={() => allNew('add')} sx={{ textTransform: 'none' }}>Add all</Button>
              <Button size="small" onClick={() => allNew('decline')} sx={{ textTransform: 'none' }}>Decline all</Button>
              <Button size="small" onClick={() => allNew(null)} sx={{ textTransform: 'none', color: 'text.secondary' }}>Clear</Button>
            </Box>
            <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse', '& td, & th': { px: 1, py: 0.75, fontSize: '0.8rem', borderBottom: '1px solid #f0f0f0', verticalAlign: 'middle' }, '& th': { fontWeight: 700, color: 'text.secondary', textAlign: 'left', bgcolor: '#fafafa' } }}>
              <thead>
                <tr><th style={{ width: 48 }} /><th>Product</th><th>Goes in</th><th style={{ width: 110 }}>Uber price</th><th style={{ width: 170 }} /></tr>
              </thead>
              <tbody>
                {newProducts.slice(0, newShown).map((p) => (
                  <tr key={p.skuKey}>
                    <td><Thumb url={p.imageUrl} /></td>
                    <td>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>{p.name}</Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>{p.sku}</Typography>
                    </td>
                    <td>{p.placements.map((pl) => `${pl.menu} › ${pl.section}`).join('; ')}</td>
                    <td>
                      {p.suggestedCents ? rand(p.suggestedCents) : <Typography component="span" variant="caption" color="error">no price</Typography>}
                      {p.price != null && <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>Lightspeed R{p.price.toFixed(2)}</Typography>}
                    </td>
                    <td>
                      <ToggleButtonGroup
                        size="small"
                        exclusive
                        value={decisions.newProducts[p.skuKey] ?? null}
                        onChange={(_, v) => setNew(p.skuKey, v)}
                        aria-label={`${p.sku} decision`}
                      >
                        <ToggleButton value="add" disabled={!p.suggestedCents} sx={{ textTransform: 'none', py: 0.25 }}>Add</ToggleButton>
                        <ToggleButton value="decline" sx={{ textTransform: 'none', py: 0.25 }}>Decline</ToggleButton>
                      </ToggleButtonGroup>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Box>
            {newProducts.length > newShown && (
              <Button size="small" onClick={() => setNewShown((n) => n + PAGE)} sx={{ mt: 1, textTransform: 'none' }}>
                Show {Math.min(PAGE, newProducts.length - newShown)} more of {newProducts.length - newShown}
              </Button>
            )}
          </AccordionDetails>
        </Accordion>
      )}

      {mismatches.length > 0 && (
        <Accordion sx={{ mb: 1.5, borderRadius: '8px !important', border: '1px solid #a5d6a7', '&:before': { display: 'none' } }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <MismatchIcon sx={{ color: GREEN, fontSize: 20 }} />
              <Typography variant="subtitle2" sx={{ fontWeight: 700, color: GREEN }}>
                {mismatches.length} product{mismatches.length === 1 ? '' : 's'} mismatching Lightspeed
              </Typography>
              <Chip size="small" label={`${countDecisions(decisions).fields} decided`} />
            </Box>
          </AccordionSummary>
          <AccordionDetails sx={{ pt: 0 }}>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
              Lightspeed changed these since they were last reconciled. Use Lightspeed's value, keep Uber's, or amend (type what Uber should show).
              A category or tag change, once taken, moves the item to the aisle and section the mappings give it.
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
              <Button size="small" onClick={() => allFields('accept')} sx={{ textTransform: 'none' }}>Use Lightspeed for all</Button>
              <Button size="small" onClick={() => allFields('keep')} sx={{ textTransform: 'none' }}>Keep Uber's for all</Button>
              <Button size="small" onClick={() => allFields(null)} sx={{ textTransform: 'none', color: 'text.secondary' }}>Clear</Button>
            </Box>
            {mismatches.slice(0, mmShown).map((m) => (
              <Box key={m.skuKey} sx={{ border: '1px solid #e0e0e0', borderRadius: 1.5, p: 1.25, mb: 1 }}>
                <Typography variant="body2" sx={{ fontWeight: 700 }}>
                  {m.name} <Typography component="span" variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>{m.sku}</Typography>
                </Typography>
                {m.fields.map((f) => {
                  const d = decisions.mismatches[m.skuKey]?.[f.field];
                  const amendable = ['name', 'description', 'image'].includes(f.field);
                  return (
                    <Box key={f.field} sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '110px 1fr 1fr 230px' }, gap: 1, alignItems: 'center', py: 0.75, borderTop: '1px dashed #eee' }}>
                      <Typography variant="caption" sx={{ fontWeight: 700 }}>{LABEL[f.field] ?? f.field}</Typography>
                      <Box>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>Uber now</Typography>
                        <Value field={f.field} value={f.uber} />
                      </Box>
                      <Box>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                          {f.field === 'gone' ? 'Lightspeed' : 'Lightspeed now'}
                        </Typography>
                        {f.field === 'gone' ? <Typography variant="body2" color="error">not in Lightspeed any more</Typography> : <Value field={f.field} value={f.now} />}
                      </Box>
                      <Box>
                        <ToggleButtonGroup
                          size="small"
                          exclusive
                          value={choice(d)}
                          onChange={(_, v) => setField(m.skuKey, f.field, v === 'amend' ? { amend: f.uber ?? '' } : v)}
                          aria-label={`${m.sku} ${f.field} decision`}
                        >
                          {f.field === 'gone' ? [
                            <ToggleButton key="remove" value="remove" sx={{ textTransform: 'none', py: 0.25 }}>Take off Uber</ToggleButton>,
                            <ToggleButton key="keep" value="keep" sx={{ textTransform: 'none', py: 0.25 }}>Keep</ToggleButton>,
                          ] : [
                            <ToggleButton key="accept" value="accept" sx={{ textTransform: 'none', py: 0.25 }}>Use Lightspeed</ToggleButton>,
                            <ToggleButton key="keep" value="keep" sx={{ textTransform: 'none', py: 0.25 }}>Keep Uber</ToggleButton>,
                            ...(amendable ? [<ToggleButton key="amend" value="amend" sx={{ textTransform: 'none', py: 0.25 }}>Amend</ToggleButton>] : []),
                          ]}
                        </ToggleButtonGroup>
                        {d && typeof d === 'object' && (
                          <TextField
                            size="small"
                            fullWidth
                            multiline={f.field === 'description'}
                            value={d.amend}
                            onChange={(e) => setField(m.skuKey, f.field, { amend: e.target.value })}
                            placeholder={f.field === 'image' ? 'Image URL' : `Uber ${LABEL[f.field]?.toLowerCase()}`}
                            inputProps={{ 'aria-label': `Amend ${m.sku} ${f.field}` }}
                            sx={{ mt: 0.75 }}
                          />
                        )}
                      </Box>
                    </Box>
                  );
                })}
              </Box>
            ))}
            {mismatches.length > mmShown && (
              <Button size="small" onClick={() => setMmShown((n) => n + PAGE)} sx={{ textTransform: 'none' }}>
                Show {Math.min(PAGE, mismatches.length - mmShown)} more of {mismatches.length - mmShown}
              </Button>
            )}
          </AccordionDetails>
        </Accordion>
      )}
    </Box>
  );
};

export default UberCatalogueReview;
